import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react'
import {
  Box,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Avatar,
  IconButton,
} from '@mui/material'
import { IntakeContext } from 'context/IntakeContext'
import { useModal } from 'components/modals'

import { apiClient } from 'utils/api/apiClient'

import {
  AccountBalance,
  Check,
  CreditCard,
  Home,
  InfoOutlined,
  Link,
  LocalHospital,
  MedicalServices,
  People,
  RequestQuote,
  Savings,
  VolunteerActivism,
  ChildCare,
} from '@mui/icons-material'
const iconMap = {
  checkingsavings: Savings,
  property: Home,
  creditcard: CreditCard,
  investment: AccountBalance,
  mortgage: Home,
  charity: VolunteerActivism,
  alotchecking: Savings,
  alotother: Savings,
  alotsavings: Savings,
  fdvv: LocalHospital,
  fdvd: LocalHospital,
  ltc: LocalHospital,
  visiondental: LocalHospital,
  extratax: RequestQuote,
  uniondues: People,
  fsa: MedicalServices,
  hsa: MedicalServices,
  vbp: MedicalServices,
  child: ChildCare,
}
const AccountIconSelector = ({ subType, ...props }) => {
  const IconComponent = iconMap[subType] || AccountBalance
  return <IconComponent {...props} />
}

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function AssetReview({ demoMode = false }) {
  const [assets, setAssets] = useState([])

  const getAccounts = useCallback(async () => {
    const {
      data: { items },
    } = await apiClient.get('api/assets')
    const foundAssets = items?.filter(({ type }) => type === 'asset') || []
    setAssets(foundAssets)
  }, [])

  useEffect(() => {
    let fetched = false
    if (fetched) return

    getAccounts()
    fetched = true
  }, [getAccounts])

  const { intakeState } = useContext(IntakeContext)
  const { openModal } = useModal()

  const isGovernmentEmployee = intakeState.governmentEmployee

  const handleClick = (callback) => {
    if (demoMode) return undefined
    return callback
  }

  const tspRoth = intakeState?.tspRothBalance || 0
  const tspTrad = intakeState?.tspBalance || 0
  // const hasRoth401k = intakeState?.roth
  // const hasTraditional401k = intakeState?.traditional
  // const roth401kBalance = intakeState?.rothBalance || 0
  // const traditional401kBalance = intakeState?.traditionalBalance || 0
  const hasTSP =
    (assets?.some(
      (item) =>
        item.externalAsset === true &&
        item.externalInstitutionName === 'Thrift Savings Plan'
    ) ||
      tspRoth > 0 ||
      tspTrad > 0) &&
    isGovernmentEmployee
  const hasRoth401k = !isGovernmentEmployee ? assets?.some(
    (item) => item.subTypeClassification === '401k-roth'
  ) : 0
  const hasTraditional401k = !isGovernmentEmployee ? assets?.some(
    (item) => item.subTypeClassification === '401k-traditional'
  ) : 0
  const roth401kBalance = hasRoth401k ? assets?.find(
    (item) => item.subTypeClassification === '401k-roth'
  )?.amount : 0
  const traditional401kBalance = hasTraditional401k ? assets?.find(
    (item) => item.subTypeClassification === '401k-traditional'
  )?.amount : 0
  const assetSum =
    (assets || []).map((a) => a.amount || 0).reduce((a, b) => a + b, 0) +
    (hasTSP ? tspRoth + tspTrad : 0)

  const tspAccounts = hasTSP
    ? [
        {
          name: 'Thrift Savings Plan (Roth)',
          amount: tspRoth,
          img: 'https://upload.wikimedia.org/wikipedia/commons/b/b6/US-ThriftSavingsPlan-Logo.svg',
          onClick: () =>
            openModal('fieldModal', {
              title: `Let's update your Thrift Savings Plan (Roth) Balance!`,
              userField: 'tspRothBalance',
              inputType: 'currencyFloat',
              fieldLabel: 'Thrift Savings Plan (Roth) Balance',
            }),
        },
        {
          name: 'Thrift Savings Plan (Traditional)',
          amount: tspTrad,
          img: 'https://upload.wikimedia.org/wikipedia/commons/b/b6/US-ThriftSavingsPlan-Logo.svg',
          onClick: () =>
            openModal('fieldModal', {
              title: `Let's update your Thrift Savings Plan (Traditional) Balance!`,
              userField: 'tspBalance',
              inputType: 'currencyFloat',
              fieldLabel: 'Thrift Savings Plan (Traditional) Balance',
            }),
        },
      ].filter((account) => account.amount > 0)
    : []

  const roth401kAccounts = roth401kBalance > 0 ? [
    {
      name: 'Roth 401k',
      amount: roth401kBalance,
    }
  ] : []

  const traditional401kAccounts = traditional401kBalance > 0 ? [
    {
      name: 'Traditional 401k',
      amount: traditional401kBalance,
    }
  ] : []

  const allAccounts = [...tspAccounts, ...assets]

  return (
    <Box p={2} width="100%" bgcolor="#fff">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant={'body1Medium'}>Total Assets</Typography>
        <Typography variant={'body1Medium'}>
          {dollarFormat.format(assetSum)}
        </Typography>
      </Box>

      {/* Assets Table */}
      <TableContainer sx={{ border: '1px solid #262627' }}>
        <Table>
          <TableBody>
            {allAccounts.map((item, index) => {
              const isTSP = !!item.onClick
              const name = item.name || item?.name || 'Unnamed Account'
              const amount = item.amount || 0
              const logo = item.img || item.externalInstitutionLogo
              const onClick = isTSP
                ? handleClick(item.onClick)
                : handleClick(() =>
                    openModal('editAccount', {
                      accountType: 'Asset',
                      account: item,
                      getAccounts,
                    })
                  )

              return (
                <TableRow
                  key={index}
                  hover
                  onClick={onClick}
                  sx={{
                    cursor: demoMode ? 'default' : 'pointer',
                    borderBottom:
                      index === allAccounts.length - 1
                        ? 'none'
                        : '1px solid #E0E0E0',

                    '&:hover': {
                      backgroundColor: demoMode ? 'inherit' : '#f9f9f9',
                    },
                  }}
                >
                  <TableCell sx={{ border: 'none', py: 1.5 }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      {logo ? (
                        <Avatar src={logo} sx={{ width: 32, height: 32 }} />
                      ) : (
                        <IconButton
                          aria-label="edit"
                          size="small"
                          sx={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: '#eee',
                          }}
                        >
                          <AccountIconSelector
                            fontSize="inherit"
                            subType={item?.subType}
                          />
                        </IconButton>
                      )}
                      <Typography>{name}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      border: 'none',
                      py: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    {dollarFormat.format(amount)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Button */}
      <Box mt={2}>
        <Button
          variant="contained"
          onClick={() => {
            if (!demoMode) {
              openModal('addAccounts', {
                getAccounts: getAccounts,
                openToPage: 'Assets',
              })
            }
          }}
          sx={{
            ml: 1.5,
            mb: 2,
            padding: '8px 24px',
            backgroundColor: '#262627',
            border: '2px solid #262627',
            color: '#fff',
            boxShadow: 'none',
            borderRadius: '100px',
            fontWeight: 600,
            fontSize: { xs: '14px', md: '18px' },
            textTransform: 'none',
            lineHeight: 1.5,
            '&:hover, &:focus, &:active, &:focus:not(:hover)': {
              transform: 'none',
              backgroundColor: '#262627fa',
              borderColor: '#262627fa',
              boxShadow: 'none',
            },
            '&:disabled': {
              borderColor: 'rgba(0,0,0,0)',
            },
          }}
        >
          + Add another Asset
        </Button>
      </Box>
    </Box>
  )
}

export default AssetReview
